import EventBus from 'eventing-bus';
import Box from '@material-ui/core/Box';
import TextField from '@mui/material/TextField';
import { useStopwatch } from 'react-timer-hook';
import React, { useState, useEffect } from 'react';
import { CircularProgress } from "@material-ui/core";
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Select, MenuItem, FormControl } from '@mui/material';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

import '../index.css';
import Functions from './Functions';
import TopSettings from './TopSettings';
const livekit = require('livekit-client');
import constants from '../../../constants';
import ViewStateManager from '../../../utils/viewStateManager';
import { getSingleAgent, updateAgent, getConnectionDetails } from '../../../store/Agent';
import { getAllPhones, updatePhone } from '../../../store/Phone';

import PhoneModal from './AddPhoneModal.js';
import CallLogs from '../../Phone/CallLogs.js';

// =======================
// Main AgentDetails Component
// =======================

/** AgentDetails - Main component for agent details page **/
const SingleAgent = ({ }) => {

    // ========== Hooks and Redux ==========
    const { id } = useParams();
    const history = useHistory()
    const dispatch = useDispatch()
    const { stats, singleAgent, connectionDetails, isLoading } = useSelector((state) => state['Agent']);
    const { allPhones } = useSelector((state) => state['Phone']);

    // ========== State Variables ==========
    const [isUpdated, setUpdated] = useState(false);
    const [agentData, setAgentData] = useState({});
    const [isEdited, setIsEdited] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [roomInstance, setRoomInstance] = useState(null);
    const [ishistoryTab, setIsHistoryTab] = useState(false);
    const [isAgentConnected, setIsAgentConnected] = useState(false);

    const [openWelcome, setOpenWelcome] = useState(false);
    const [searchWelcome, setSearchWelcome] = useState('');
    const [variables, setVariables] = useState([{ name: '', value: '' }]);

    const [speakingState, setSpeakingState] = useState('Connecting');
    const { seconds, minutes, isRunning, start, reset } = useStopwatch({ autoStart: false });
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    const [isPhoneModal, setPhoneModal] = useState(false);
    const togglePhoneModal = () => setPhoneModal(prev => !prev);

    const [activeSidebarTab, setActiveSidebarTab] = useState('prompt');

    // ========== Side Effects ==========
    useEffect(() => {
        dispatch({ type: 'CLEAR_CONNECTION_DETAILS' });
        if (id) dispatch(getSingleAgent(id));
        dispatch(getAllPhones());
    }, []);

    useEffect(() => {
        setAgentData(singleAgent);
        if (singleAgent?.dynamicVariables) {
            const variablesArray = Object.entries(singleAgent.dynamicVariables).map(([key, value]) => ({
                name: key,
                value: value
            }));
            setVariables(variablesArray.length > 0 ? variablesArray : [{ name: '', value: '' }]);
        } else if (singleAgent && Object.keys(singleAgent).length > 0) {
            setVariables([{ name: '', value: '' }]);
        }
    }, [singleAgent]);
    useEffect(() => { if (isUpdated) handleUpdateAgent() }, [agentData?.['language'], agentData?.['modelType'], agentData?.['webhookURL'], agentData?.['dynamicVariables'], agentData?.['welcomeMessageType'], agentData?.['llmTemperature']]);
    useEffect(() => { if (connectionDetails && isAgentConnected) initializeAgent(connectionDetails) }, [connectionDetails]);

    // ========== Initialize Agent ==========
    const initializeAgent = async (connectionDetails) => {
        if (!connectionDetails) return console.error('No connection details available');
        try {
            const { serverUrl, participantToken } = connectionDetails;

            const room = new livekit.Room();
            await room.connect(serverUrl, participantToken);
            console.log('Connected to room:', room.name);

            await room.localParticipant.setMicrophoneEnabled(true);
            console.log('Microphone enabled');

            start();

            room.on('activeSpeakersChanged', (speakers) => { handleActiveSpeakerChange(speakers) });

            room.on('trackSubscribed', (track) => {
                if (track.kind === 'audio') {
                    const audioElement = track.attach();
                    document.body.appendChild(audioElement);
                }
            });
            setRoomInstance(room);
            setIsAgentConnected(true);
        } catch (error) {
            console.error('Error connecting to LiveKit:', error);
            setIsAgentConnected(false);
        }
    }

    const handleActiveSpeakerChange = (speakers) => {
        speakers.forEach(speaker => {
            const isAgent = speaker.identity.includes('voice_assistant');
            if (isAgent) setSpeakingState('Listening');
            else setSpeakingState('Speaking');
        });
        if (speakers.length === 0) setSpeakingState('Thinking');
    }

    // ========== Start Agent ==========
    const startAgent = async () => {
        try {
            setIsAgentConnected(true)
            dispatch(getConnectionDetails({ agentId: agentData['_id'], callType: "voiceagent-call" }));
            setRoomInstance(null);
        } catch (error) { return console.error('Error fetching connection details:', error) }
    }

    // ========== Disconnect Room ==========
    const disconnectAgent = () => {
        if (roomInstance) {
            roomInstance.disconnect();
            setRoomInstance(null);
            reset(new Date(), false);
            setIsAgentConnected(false);
            setSpeakingState('Connecting');
            console.log('Previous room disconnected');
        }
    }

    // ========== Update Agent ==========
    const handleUpdateAgent = (file = selectedFile) => {
        setUpdated(false);
        const formData = new FormData();
        const agentKeys = ['name', 'script', 'firstMessage', 'welcomeMessageType', 'modelType', 'language', 'webhookURL', 'dynamicVariables', 'llmTemperature'];
        for (const key of agentKeys) if (agentData[key] !== undefined)
            if (agentData[key] && typeof agentData[key] === 'object') formData.append(key, JSON.stringify(agentData[key]));
            else formData.append(key, agentData[key]);

        if (agentData['webhookURL'] === '') formData.append('webhookURL', '');

        formData.append('voiceId', agentData['voiceId']?.['_id'] || '');
        formData.append('modelId', agentData['modelId']?.['_id'] || '');

        if (file) formData.append('file', file);

        dispatch(updateAgent(id, formData, () => setSelectedFile(null), () => console.log('Update failed')));
    }

    const filteredWelcomeMessage = constants['welcomeMessage'].filter((status) => status['label'].toLowerCase().includes(searchWelcome.toLowerCase()));
    const handleWelcomeMessage = (value) => {
        setSearchWelcome('');
        setOpenWelcome(false);
        const selectedOption = constants['welcomeMessage'].find((option) => option['label'] === value);
        if (selectedOption) {
            setAgentData({ ...agentData, welcomeMessageType: selectedOption.value });
            setUpdated(true);
        }
    };

    // ======== Color Classes based on Cost & Latency ========
    const costTotal = parseFloat(stats?.['cost']?.['total'] || 0);
    let costClass = 'green-bg';
    if (costTotal > 0.075) costClass = 'orange-bg';
    else if (costTotal > 0.1) costClass = 'red-bg';

    const latencyTotal = parseFloat(stats?.['latency']?.['total'] || 0);
    let latencyClass = 'green-bg';
    if (latencyTotal > 1000) latencyClass = 'red-bg';
    else if (latencyTotal > 750) latencyClass = 'orange-bg';

    const [showVariablesBox, setShowVariablesBox] = useState(false);
    const [expandedAgent, setExpandedAgent] = useState(false);

    const toggleContentVeriablesBox = () => setShowVariablesBox(!showVariablesBox);

    const handleVariablesAction = (action, index = null, field = null, value = null) => {
        switch (action) {
            case 'add':
                setVariables(prev => [...prev, { name: '', value: '' }]);
                break;

            case 'remove':
                if (index !== null) setVariables(prev => prev.filter((_, i) => i !== index));
                break;

            case 'change':
                if (index !== null && field && value !== null) {
                    setVariables(prev => {
                        const updated = [...prev];
                        updated[index][field] = value;
                        return updated;
                    });
                }
                break;

            case 'save':
                const dynamicVariablesObject = {};
                const duplicateNames = [];
                const variableNames = new Set();

                variables.forEach((item) => {
                    const trimmedName = item.name.trim();
                    const trimmedValue = item.value.trim();

                    if (trimmedName && trimmedValue) {
                        if (variableNames.has(trimmedName)) duplicateNames.push(trimmedName);
                        else variableNames.add(trimmedName), dynamicVariablesObject[trimmedName] = trimmedValue;
                    }
                });

                if (duplicateNames.length > 0) {
                    EventBus.publish('error', `Duplicate variable names found: ${duplicateNames.join(', ')}`);
                    return;
                }

                setAgentData(prev => ({ ...prev, dynamicVariables: dynamicVariablesObject }));
                setUpdated(true);
                setShowVariablesBox(false);
                break;

            case 'cancel':
                setShowVariablesBox(false);
                break;

            default:
                console.warn('Unknown variables action:', action);
        }
    };

    useEffect(() => {
        document.body.classList.add("hide-layout");
        return () => {
            document.body.classList.remove("hide-layout");
        };
    }, []);

    const handleBackClick = () => {
        history.push('/agents');
        document.body.classList.remove("hide-layout");
        localStorage.removeItem("hide-layout");
    };

    return (
        <div className='sp-layout'>
            {/* Header */}
            <div className='sp-header'>
                <div className='sp-header-left'>
                    <button onClick={handleBackClick} className='sp-back-btn'>← Back</button>
                    {isEditing ? (
                        <input
                            autoFocus
                            type='text'
                            name='name'
                            className='sp-name-edit'
                            value={agentData?.['name'] || ''}
                            onBlur={() => { setIsEditing(false); handleUpdateAgent(); }}
                            onChange={(e) => setAgentData({ ...agentData, [e['target']['name']]: e['target']['value'] })}
                        />
                    ) : (
                        <span className='sp-agent-name' onClick={() => setIsEditing(true)}>
                            {isLoading ? 'Loading...' : (agentData['name'] || 'Agent Name')} ✏️
                        </span>
                    )}
                    <div className='sp-chips'>
                        <div className={`sp-chip ${costClass}`}>${parseFloat(costTotal).toFixed(2) || 0}/min</div>
                        <div className={`sp-chip ${latencyClass}`}>{latencyTotal || 0}ms</div>
                        <div className='sp-chip'>
                            ID: {agentData?._id ? `${agentData._id.slice(0, 4)}...${agentData._id.slice(-4)}` : '...'}
                            <CopyToClipboard text={agentData?._id} onCopy={() => EventBus.publish('success', 'Address Copied!')}>
                                <span className='sp-copy-btn' style={{marginLeft:'5px', cursor:'pointer'}}>(Copy)</span>
                            </CopyToClipboard>
                        </div>
                    </div>
                </div>
                <div className='sp-header-right'>
                    <button className="btn-style-new" onClick={togglePhoneModal}>☎ Phone</button>
                </div>
            </div>

            {/* Horizontal Tab Bar */}
            <div className='sp-tab-bar'>
                <button className={`sp-tab ${activeSidebarTab === 'prompt' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('prompt')}>
                    <span className='sp-tab-icon'>✨</span>Prompt
                </button>
                <button className={`sp-tab ${activeSidebarTab === 'voice' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('voice')}>
                    <span className='sp-tab-icon'>🗣️</span>Voice &amp; Model
                </button>
                <button className={`sp-tab ${activeSidebarTab === 'functions' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('functions')}>
                    <span className='sp-tab-icon'>⚡</span>Functions
                </button>
                <button className={`sp-tab ${activeSidebarTab === 'history' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('history')}>
                    <span className='sp-tab-icon'>🕒</span>Call History
                </button>
            </div>

            {/* Body: Content Pane + Test Panel */}
            <div className='sp-body'>
                {/* Left: Content */}
                <div className='sp-content'>
                    {activeSidebarTab === 'prompt' && (
                        <>
                            <div className='sp-section'>
                                <div className='sp-section-header'>
                                    <h3>System Prompt</h3>
                                    {isEdited && (
                                        <button className='btn-style-new' onClick={() => { handleUpdateAgent(); setIsEdited(false); }}>Save Changes</button>
                                    )}
                                </div>
                                <div
                                    className='sp-prompt-editor'
                                    contentEditable
                                    suppressContentEditableWarning
                                    onBlur={(e) => {
                                        const updatedScript = e['target']['innerHTML'].trim() || ' ';
                                        setAgentData((prev) => {
                                            if (prev['script'] !== updatedScript) setIsEdited(true);
                                            return { ...prev, script: updatedScript };
                                        });
                                    }}
                                    onInput={() => setIsEdited(true)}
                                    dangerouslySetInnerHTML={{ __html: agentData?.['script'] || '' }}
                                />
                            </div>
                            <div className='sp-section'>
                                <div className='sp-section-header'>
                                    <h3>Starting Message</h3>
                                </div>
                                <div className='group-from' style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '12px', color: '#888', marginBottom: '8px', display: 'block' }}>First Message Type</label>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={constants['welcomeMessage'].find(opt => opt.value === agentData?.['welcomeMessageType'])?.label || ''}
                                            onChange={(e) => handleWelcomeMessage(e.target.value)}
                                            displayEmpty
                                            variant="outlined"
                                            sx={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff' }}
                                        >
                                            {constants['welcomeMessage'].map((option) => (
                                                <MenuItem key={option.value} value={option.label}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                                {agentData?.['welcomeMessageType'] === 'defined' && (
                                    <div className='group-from'>
                                        <label style={{ fontSize: '12px', color: '#888', marginBottom: '8px', display: 'block' }}>First Message</label>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            placeholder="Hello! This is Sales AI, how can I help you today?"
                                            value={agentData?.['firstMessage'] || ''}
                                            onChange={(e) => { setAgentData({ ...agentData, firstMessage: e.target.value }); setIsEdited(true); }}
                                            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '13px' } }}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {activeSidebarTab === 'voice' && (
                        <div className='sp-section sp-section-flush'>
                            <TopSettings agentData={agentData} setAgentData={setAgentData} setUpdated={setUpdated} />
                        </div>
                    )}

                    {activeSidebarTab === 'functions' && (
                        <div className='sp-section sp-section-flush'>
                            <Functions
                                activeSidebarTab={activeSidebarTab}
                                agentId={id}
                                agentData={agentData}
                                setAgentData={setAgentData}
                                handleUpdateAgent={handleUpdateAgent}
                                setSelectedFile={setSelectedFile}
                            />
                        </div>
                    )}

                    {activeSidebarTab === 'history' && (
                        <CallLogs agentId={id} />
                    )}
                </div>

                {/* Right: Test Panel */}
                <div className='sp-test-panel'>
                    <div className='sp-test-header'>
                        <h3>Test Agent</h3>
                    </div>
                    <div className='sp-test-body'>
                        {!isAgentConnected ? (
                            <div className='talk-box'>
                                <p>{agentData?.status === 'inactive' ? 'Agent is deactivated' : isLoading ? 'Loading agent...' : 'Test your agent'}</p>
                                <button
                                    className='btn-style-border'
                                    onClick={() => {
                                        if (!agentData?._id) return EventBus.publish('error', 'Please wait for agent data to load.');
                                        if (agentData?.status === 'inactive') return EventBus.publish('error', 'Agent is deactivated.');
                                        startAgent();
                                    }}
                                    disabled={isLoading || !agentData?._id || agentData?.status === 'inactive'}
                                >
                                    {isLoading ? 'Loading...' : 'Start Test'}
                                </button>
                            </div>
                        ) : (
                            <div className='talk-box style-two'>
                                <div className={`state-${speakingState.toLowerCase()}`}>
                                    <div className="state-dot"></div>
                                    <span className="state-text">{speakingState}</span>
                                    <div className="state-wave"></div>
                                </div>
                                {isRunning && <div className='timer-agent'><p>{formattedMinutes}:{formattedSeconds}</p></div>}
                                <button className='btn-style-red' onClick={disconnectAgent}>End Call</button>
                            </div>
                        )}
                    </div>
                    <div className='sp-test-vars'>
                        <button className='btn-veriables' onClick={toggleContentVeriablesBox}>Dynamic Variables &#123; &#125;</button>
                        {showVariablesBox && (
                            <div className="veriables-area" style={{marginTop: '10px'}}>
                                {variables.map((item, index) => (
                                    <div className='values-inputs' key={index} style={{display:'flex', gap:'5px', marginBottom:'5px'}}>
                                        <TextField size='small' placeholder='Name' value={item.name} onChange={(e) => handleVariablesAction('change', index, 'name', e.target.value)} />
                                        <TextField size='small' placeholder='Value' value={item.value} onChange={(e) => handleVariablesAction('change', index, 'value', e.target.value)} />
                                        <button onClick={() => handleVariablesAction('remove', index)}>✕</button>
                                    </div>
                                ))}
                                <button onClick={() => handleVariablesAction('add')}>+ Add</button>
                                <button onClick={() => handleVariablesAction('save')}>Save</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <PhoneModal
                isPhoneModal={isPhoneModal}
                togglePhoneModal={togglePhoneModal}
                phones={allPhones}
                agentId={id}
                agentData={agentData}
                onPhoneAssign={(phoneData) => {
                    setAgentData(prev => ({
                        ...prev,
                        phone: phoneData.phoneNumber,
                        inboundAgentId: phoneData.inboundAgentId,
                        outboundAgentId: phoneData.outboundAgentId
                    }));
                    setUpdated(true);
                }}
            />
        </div>
    );
}

export default SingleAgent
